import { Check, ChevronDown } from 'lucide-react';
import { useEffect, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';

import { Button, Input, useToast } from '@/components/ui';
import { ROUTES } from '@/constants';
import { useCarriersQuery, usePlantsQuery } from '@/hooks/auth';
import { signUp } from '@/service/AuthenticationService';
import {
  useAppSelector,
  selectMobileNumber,
  selectCountryCode,
  selectCarriers,
  selectPlants,
} from '@/store';
import { cn } from '@/utils/cn';

// ─────────────────────────────────────────────────────────────────
// Niagara Logo Component
// ─────────────────────────────────────────────────────────────────

function NiagaraLogo() {
  return <img src="/images/logo_niagara.svg" alt="Niagara" className="h-8 sm:h-10 2xl:h-12" />;
}

// ─────────────────────────────────────────────────────────────────
// Plants Dropdown Component
// ─────────────────────────────────────────────────────────────────

interface PlantOption {
  _id: string;
  plant_name: string;
}

interface PlantsDropdownProps {
  selectedPlantIds: string[];
  onTogglePlant: (plantId: string) => void;
  allPlants: PlantOption[];
  disabled?: boolean | undefined;
  error?: boolean | undefined;
  errorMessage?: string | undefined;
}

function PlantsDropdown({
  selectedPlantIds,
  onTogglePlant,
  allPlants,
  disabled,
}: PlantsDropdownProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter plants locally based on search text
  const filteredPlants = search
    ? allPlants.filter((p) => p.plant_name.toLowerCase().includes(search.toLowerCase().trim()))
    : allPlants;

  // Resolve selected IDs to display names
  const selectedNames = selectedPlantIds
    .map((id) => allPlants.find((p) => p._id === id)?.plant_name)
    .filter(Boolean)
    .join(', ');

  // When open show search text, when closed show selected plant names
  const inputValue = isOpen ? search : selectedNames;
  const placeholder = t('signup.searchPlant');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);

    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleFocus = () => {
    if (!isOpen) {
      setIsOpen(true);
      setSearch('');
    }
  };

  return (
    <div className="w-full" ref={dropdownRef}>
      <div
        className={cn(
          'flex h-auto w-full items-center bg-transparent px-3 py-3 text-base',
          'border-[1.8px] border-[#657E90] rounded-lg',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={!isOpen}
          className={cn(
            'flex-1 h-full bg-transparent outline-none text-[13.5px] text-[#212121] placeholder:text-[#636363] placeholder:text-[14.5px] placeholder:font-light placeholder:font-poppins min-w-0',
            !isOpen && 'truncate cursor-pointer'
          )}
          aria-label={t('signup.plants')}
          autoComplete="off"
        />
        <ChevronDown
          className={cn(
            'font-bold h-6 w-6 text-gray-500 text-xl flex-shrink-0 ml-2 transition-transform cursor-pointer',
            isOpen && 'rotate-180'
          )}
          onClick={() => {
            if (!disabled) {
              if (isOpen) {
                setIsOpen(false);
                setSearch('');
              } else {
                setIsOpen(true);
                setSearch('');
                inputRef.current?.focus();
              }
            }
          }}
        />
      </div>

      {isOpen && (
        <div className="absolute z-50 mt-1 left-0 right-0 rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="max-h-48 overflow-y-auto p-1">
            {filteredPlants.length === 0 ? (
              <div className="px-3 py-4 text-base text-gray-500 text-center">
                {t('signup.noPlantsFound')}
              </div>
            ) : (
              filteredPlants.map((plant) => {
                const isSelected = selectedPlantIds.includes(plant._id);
                return (
                  <button
                    key={plant._id}
                    type="button"
                    onClick={() => {
                      onTogglePlant(plant._id);
                    }}
                    className={cn(
                      'flex w-full items-center justify-between rounded-sm px-3 py-2.5 text-sm text-left transition-colors',
                      isSelected ? 'bg-[#cee0ed]' : 'hover:bg-gray-100'
                    )}
                  >
                    <span className={cn('truncate', isSelected && 'font-medium text-black')}>
                      {plant.plant_name}
                    </span>
                    {isSelected && (
                      <Check
                        className="h-4 w-4 text-[#025f9e] font-bold flex-shrink-0 ml-2"
                        strokeWidth={4}
                      />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Carrier Dropdown Component (Single-select)
// ─────────────────────────────────────────────────────────────────

interface CarrierOption {
  _id: string;
  carrier_name: string;
}

interface CarrierDropdownProps {
  selectedCarrierId: string;
  onSelectCarrier: (carrierId: string) => void;
  allCarriers: CarrierOption[];
  disabled?: boolean | undefined;
  error?: boolean | undefined;
  errorMessage?: string | undefined;
}

function CarrierDropdown({
  selectedCarrierId,
  onSelectCarrier,
  allCarriers,
  disabled,
  error,
  errorMessage,
}: CarrierDropdownProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter carriers locally based on search text
  const filteredCarriers = search.trim()
    ? allCarriers.filter((c) => c.carrier_name.toLowerCase().includes(search.toLowerCase().trim()))
    : allCarriers;

  // Resolve selected ID to display name
  const selectedName = allCarriers.find((c) => c._id === selectedCarrierId)?.carrier_name ?? '';

  // When open show search text, when closed show selected carrier
  const inputValue = isOpen ? search : selectedName;
  const placeholder = t('signup.searchCarrier');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleFocus = () => {
    if (!isOpen) {
      setIsOpen(true);
      setSearch('');
    }
  };

  return (
    <div className="w-full" ref={dropdownRef}>
      <div
        className={cn(
          'flex h-auto w-full items-center bg-transparent px-3 py-3 text-base 2xl:text-base',
          'border-[1.8px] border-[#657E90] rounded-lg',
          error ? 'border-red-500' : 'border-[#657E90]',
          disabled && 'cursor-not-allowed opacity-50'
        )}
      >
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={!isOpen}
          className={cn(
            'flex-1 h-full bg-transparent outline-none text-base text-black placeholder:text-[#415B6D] placeholder:text-[15px] placeholder:font-base placeholder:font-poppins  min-w-0',
            !isOpen && 'truncate cursor-pointer'
          )}
          aria-label={t('signup.carrier')}
          autoComplete="off"
        />
        <ChevronDown
          className={cn(
            'h-6 w-6 text-gray-500 flex-shrink-0 ml-2 transition-transform cursor-pointer',
            isOpen && 'rotate-180'
          )}
          onClick={() => {
            if (!disabled) {
              if (isOpen) {
                setIsOpen(false);
                setSearch('');
              } else {
                setIsOpen(true);
                setSearch('');
                inputRef.current?.focus();
              }
            }
          }}
        />
      </div>

      {errorMessage && <p className="mt-1 text-xs text-red-500">{errorMessage}</p>}

      {/* Dropdown list */}
      {isOpen && (
        <div className="absolute z-50 mt-1 left-0 right-0 rounded-md border border-gray-200 bg-white shadow-lg">
          <div className="max-h-48 overflow-y-auto p-1">
            {filteredCarriers.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-500 text-center">
                {t('signup.noCarriersFound')}
              </div>
            ) : (
              filteredCarriers.map((carrierOption) => {
                const isSelected = selectedCarrierId === carrierOption._id;
                return (
                  <button
                    key={carrierOption._id}
                    type="button"
                    onClick={() => {
                      onSelectCarrier(carrierOption._id);
                      setIsOpen(false);
                      setSearch('');
                    }}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-sm px-3 py-2.5 text-sm text-left transition-colors',
                      isSelected ? 'bg-[#cfe1ee]' : 'hover:bg-gray-100'
                    )}
                  >
                    <span className={cn('truncate', isSelected && 'font-medium text-black')}>
                      {carrierOption.carrier_name}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const VALID_NAME_REGEX = /^(?=.*[a-zA-Z])[a-zA-Z'-]+$/;

// ─────────────────────────────────────────────────────────────────
// Signup Form Component
// ─────────────────────────────────────────────────────────────────

function SignupForm() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { t } = useTranslation();
  // Fetch plants & carriers so they survive page refresh (LoginPage may not have mounted)
  usePlantsQuery();
  useCarriersQuery();

  // Auth state (pre-populated from login flow)
  const authMobileNumber = useAppSelector(selectMobileNumber);
  const authCountryCode = useAppSelector(selectCountryCode);
  const plantsList = useAppSelector(selectPlants);
  const carriersList = useAppSelector(selectCarriers);

  // Local form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [carrier, setCarrier] = useState('');
  const [selectedPlants, setSelectedPlants] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [lastNameError, setLastNameError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Validate form
      const missingFields: string[] = [];
      if (!firstName.trim()) missingFields.push(t('signup.firstName'));
      if (!lastName.trim()) missingFields.push(t('signup.lastName'));
      if (!carrier) missingFields.push(t('signup.carrier'));
      if (selectedPlants.length === 0) missingFields.push(t('signup.plants'));

      if (missingFields.length > 0) {
        let message = '';
        if (missingFields.length === 1) {
          message = `${missingFields[0] ?? ''} ${t('signup.isRequired')}`;
        } else if (missingFields.length === 2) {
          message = `${missingFields[0] ?? ''} & ${missingFields[1] ?? ''} ${t('signup.areRequired')}`;
        } else {
          const last = missingFields.pop() ?? '';
          message = `${missingFields.join(', ')} & ${last} ${t('signup.areRequired')}`;
        }

        showToast(message, { type: 'error' });
        return;
      }

      const firstNameInvalid = firstName.trim() !== '' && !VALID_NAME_REGEX.test(firstName.trim());
      const lastNameInvalid = lastName.trim() !== '' && !VALID_NAME_REGEX.test(lastName.trim());
      setFirstNameError(firstNameInvalid ? t('signup.invalidName') : null);
      setLastNameError(lastNameInvalid ? t('signup.invalidName') : null);
      if (firstNameInvalid || lastNameInvalid) {
        showToast(t('signup.invalidName'), { type: 'error' });
        return;
      }

      // VULN-03: Validate email format if provided
      if (email.trim() && !EMAIL_REGEX.test(email.trim())) {
        showToast(t('signup.invalidEmail'), { type: 'error' });
        return;
      }

      setIsSubmitting(true);
      setSubmitError(null);

      try {
        const response = await signUp({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          country_code: authCountryCode,
          mobile_no: authMobileNumber,
          email: email.trim(),
          carrier,
          plants: selectedPlants,
        });

        if (!response.status) {
          const userMessage = response.message || t('signup.signUpFailed');
          setSubmitError(userMessage);
          showToast(userMessage, { type: 'error' });
          return;
        }

        if (!response.registered) {
          const userMessage = response.message || t('signup.registrationNotCompleted');
          setSubmitError(userMessage);
          showToast(userMessage, { type: 'error' });
          return;
        }

        showToast(
          t('otp.otpSentSuccess', { phoneNumber: `${authCountryCode}${authMobileNumber}` })
        );
        navigate(ROUTES.otp);
      } catch (error) {
        const isAbort = error instanceof DOMException && error.name === 'AbortError';
        if (!isAbort) {
          let userMessage = t('signup.signUpFailed');
          if (typeof error === 'object' && error !== null && 'response' in error) {
            const axiosErr = error as { response?: { data?: { message?: string } } };
            const backendMsg = axiosErr.response?.data?.message;
            if (backendMsg) {
              userMessage = backendMsg;
            }
          }
          setSubmitError(userMessage);
          showToast(userMessage, { type: 'error' });
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      firstName,
      lastName,
      authCountryCode,
      authMobileNumber,
      email,
      carrier,
      selectedPlants,
      showToast,
      navigate,
      t,
    ]
  );

  const handleSelectCarrier = useCallback((carrierId: string) => {
    setCarrier(carrierId);
  }, []);

  const handleTogglePlant = useCallback((plantId: string) => {
    setSelectedPlants((prev) =>
      prev.includes(plantId) ? prev.filter((id) => id !== plantId) : [...prev, plantId]
    );
  }, []);

  return (
    <div className="h-screen relative overflow-hidden">
      {/* Background: 60% blue, 40% white */}
      <div className="absolute inset-0 flex flex-col">
        <div className="h-[60%] bg-gradient-to-br from-[#094888] to-[#0B1B25]" />
        <div className="h-[40%] bg-white" />
      </div>

      {/* Main layout */}
      <div className="relative z-10 h-full flex flex-col">
        {/* Container 1: Header - Niagara logo + Sign up to continue */}
        <div className="flex-shrink-0 pt-3 pb-4 px-4 sm:pt-4 sm:pb-6 sm:px-6 2xl:pt-6 2xl:pb-8 2xl:px-10 text-left">
          <NiagaraLogo />
          <p className="text-white text-base 2xl:text-base">{t('signup.title')}</p>
        </div>

        {/* Container 2: Signup form card - scrollable inside */}
        <div className="flex-1 min-h-0 mx-[10%] mb-2 sm:mb-4">
          <div className="h-full 3xl:h-auto 3xl:max-h-full flex flex-col bg-gray-100 rounded-lg sm:rounded-xl shadow-transparent p-3 sm:p-4 md:p-6">
            <div className="flex-1 min-h-0 overflow-y-auto [&::-webkit-scrollbar]:w-[2px] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-500 [&::-webkit-scrollbar-track]:bg-transparent">
              <div className="px-1 pt-2 pb-2">
                {/* API error message */}
                {submitError && (
                  <div className="rounded-lg bg-red-50 text-red-600 text-xs p-3 text-center mb-10">
                    {submitError}
                  </div>
                )}

                <form
                  onSubmit={handleSubmit}
                  className="font-poppins space-y-3 sm:space-y-4 2xl:space-y-5"
                >
                  {/* First Name */}
                  <div className="">
                    <label
                      htmlFor="firstName"
                      className="block text-base 2xl:text-base font-light font-poppins text-[#537184] mb-1"
                    >
                      {t('signup.firstName')} <span className="text-black">*</span>
                    </label>
                    <input
                      id="firstName"
                      type="text"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value.replace(/[^a-zA-Z'-]/g, ''));
                        if (firstNameError) setFirstNameError(null);
                      }}
                      placeholder={t('signup.enterFirstName')}
                      maxLength={20}
                      disabled={isSubmitting}
                      className={cn(
                        'flex h-auto w-full rounded-lg border-[1.8px] bg-transparent px-3 py-3 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-[#415B6D] placeholder:text-[15px] placeholder:font-light placeholder:font-poppins focus-visible:outline-none',
                        firstNameError ? 'border-red-500' : 'border-[#657E90]'
                      )}
                      autoComplete="given-name"
                    />
                    {firstNameError && (
                      <p className="mt-1 text-xs text-red-500">{firstNameError}</p>
                    )}
                  </div>

                  {/* Last Name */}
                  <div>
                    <label
                      htmlFor="lastName"
                      className="block text-base 2xl:text-base font-light font-poppins text-[#537184] mb-1"
                    >
                      {t('signup.lastName')} <span className="text-black">*</span>
                    </label>
                    <input
                      id="lastName"
                      type="text"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value.replace(/[^a-zA-Z'-]/g, ''));
                        if (lastNameError) setLastNameError(null);
                      }}
                      placeholder={t('signup.enterLastName')}
                      maxLength={20}
                      disabled={isSubmitting}
                      className={cn(
                        'flex h-auto w-full rounded-lg border-[1.8px] bg-transparent px-3 py-3 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-[#415B6D] placeholder:text-[15px] placeholder:font-light placeholder:font-poppins focus-visible:outline-none',
                        lastNameError ? 'border-red-500' : 'border-[#657E90]'
                      )}
                      autoComplete="family-name"
                    />
                    {lastNameError && <p className="mt-1 text-xs text-red-500">{lastNameError}</p>}
                  </div>

                  {/* Mobile Number (pre-populated from login, disabled) */}
                  <div>
                    <label
                      htmlFor="mobileNumber"
                      className="block text-sm sm:text-sm 2xl:text-base font-light font-poppins text-[#537184] mb-1"
                    >
                      {t('signup.mobileNumber')} <span className="text-black">*</span>
                    </label>
                    <Input
                      id="mobileNumber"
                      type="tel"
                      value={`(${authCountryCode}) ${authMobileNumber}`}
                      disabled
                      className="flex h-auto w-full bg-[#aaaaaa] text-[#000000] rounded-lg border-[1.8px] disabled:opacity-100 border-[#657E90] px-3 py-3 text-base file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-[#415B6D] placeholder:text-[15px] placeholder:font-light placeholder:font-poppins focus-visible:outline-none"
                      autoComplete="tel"
                    />
                  </div>

                  {/* Plants (Multi-select Dropdown) */}
                  <div className="relative">
                    <label
                      htmlFor="plants-dropdown"
                      className="block text-base 2xl:text-base font-light font-poppins text-[#537184] mb-1"
                    >
                      {t('signup.plants')} <span className="text-black">*</span>
                    </label>
                    <PlantsDropdown
                      selectedPlantIds={selectedPlants}
                      onTogglePlant={handleTogglePlant}
                      allPlants={plantsList}
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Carrier (Single-select Dropdown) */}
                  <div className="relative">
                    <label
                      htmlFor="carrier-dropdown"
                      className="block text-sm sm:text-sm 2xl:text-base font-light font-poppins text-[#537184] mb-1"
                    >
                      {t('signup.carrier')} <span className="text-black">*</span>
                    </label>
                    <CarrierDropdown
                      selectedCarrierId={carrier}
                      onSelectCarrier={handleSelectCarrier}
                      allCarriers={carriersList}
                      disabled={isSubmitting}
                    />
                  </div>

                  {/* Email (Optional) */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-base 2xl:text-base font-light font-poppins text-[#537184] mb-1"
                    >
                      {t('signup.email')}{' '}
                      <span className="text-base 2xl:text-base font-light font-poppins text-[#537184]">
                        {t('signup.emailOptional')}
                      </span>
                    </label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                      }}
                      placeholder={t('signup.enterEmail')}
                      disabled={isSubmitting}
                      className="h-[40px] sm:h-[44px] 2xl:h-[50px] 2xl:text-base text-black placeholder:text-[#415B6D] placeholder:text-[15px] border-[#657E90]"
                      autoComplete="email"
                    />
                  </div>

                  {/* Sign Up Button - full width matching inputs */}
                  <div className="pt-3 px-3 sm:pt-4 sm:px-6 flex justify-center">
                    <Button
                      type="submit"
                      className="w-full h-10 sm:h-12 2xl:h-14 bg-[#1357A0] hover:bg-[#0f4a8a] text-white text-sm sm:text-base 2xl:text-lg font-medium rounded-lg"
                      disabled={isSubmitting}
                      isLoading={isSubmitting}
                      loadingText={t('signup.signingUp')}
                    >
                      {t('signup.signUp')}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
            {/* Already have an account - fixed at bottom inside card */}
            <div className="flex-shrink-0 pt-3">
              <p className="text-center text-base 2xl:text-base 3xl:text-lg font-normal font-poppins text-gray-500">
                {t('signup.alreadyHaveAccount')}{' '}
                <Link to={ROUTES.login} className="text-[#1357A0] font-bold hover:underline">
                  {t('signup.signIn')}
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Container 3: Truck illustration - bottom right */}
        <div className="flex justify-end xs:h-10 sm:h-12 md:h-14 lg:h-16 2xl:h-20 3xl:h-40 pr-4 sm:pr-6 2xl:pr-10 3xl:pr-16">
          <img
            src="/images/signup_truck.jpg"
            alt=""
            className="object-contain mix-blend-multiply"
            aria-hidden="true"
          />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main SignupPage Component
// ─────────────────────────────────────────────────────────────────

export function SignupPage() {
  return <SignupForm />;
}
